import classNames from 'classnames';
import { ReactNode } from 'react';

interface FormActionsProps {
  children: ReactNode;
  className?: string;
  /**
   * Pin the bar to the bottom of the nearest scroll container. Correct inside
   * a FormDialog, which owns its own scroller — hence the default.
   *
   * Pass `false` for a form in normal page flow. A page-level form has no
   * scroll container of its own, so `sticky bottom-0` resolves against the
   * viewport and the bar floats over whatever follows it; a page that stacks
   * several forms would get one such bar per form, all pinned at once.
   */
  sticky?: boolean;
}

/**
 * Action bar for a form. Holds Save/Cancel as the last child of the <form>.
 *
 * The half that is NOT optional is the responsive contract: below `sm:` the
 * buttons stack full width, and from `sm:` up they sit in a right-aligned row.
 * `[&>button]:w-full` is parent-scoped, so it outranks any width utility on the
 * button itself by specificity rather than by emit order — this repo has no
 * tailwind-merge, so a rule that depended on order would be fragile.
 */
export const FormActions = ({ children, className, sticky = true }: FormActionsProps) => (
  <div
    className={classNames(
      'bg-white',
      sticky && 'sticky bottom-0 z-0',
      'pt-4 pb-2 border-t border-grey-800',
      'flex flex-col gap-3 sm:flex-row sm:justify-end sm:gap-4',
      '[&>button]:w-full sm:[&>button]:w-auto',
      className
    )}
  >
    {children}
  </div>
);
